import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import SplitPanelContent from '../../../components/SplitPanelContent';

// ── Helper: darken a hex colour by a given amount (0–255) ────────────────────
const darkenHex = (hex, amount = 40) => {
  const clean = hex?.replace('#', '') || '';
  if (clean.length !== 6) return '#1e293b';
  const r = Math.max(0, parseInt(clean.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(clean.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(clean.slice(4, 6), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// ── Helper: load a Google Font dynamically ────────────────────────────────────
const GOOGLE_FONT_MAP = {
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@300;400;500;600;700',
  'DM Sans': 'DM+Sans:wght@300;400;500;600;700',
  'Manrope': 'Manrope:wght@300;400;500;600;700',
  'Inter': 'Inter:wght@300;400;500;600;700',
  'Roboto': 'Roboto:wght@300;400;500;700',
  'Lato': 'Lato:wght@300;400;700',
  'Poppins': 'Poppins:wght@300;400;500;600;700',
  'Nunito': 'Nunito:wght@300;400;500;600;700',
  'Open Sans': 'Open+Sans:wght@300;400;500;600;700',
  'JetBrains Mono': 'JetBrains+Mono:wght@300;400;500;600;700',
};

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || !GOOGLE_FONT_MAP?.[fontFamily]) return;
  const id = `gfont-${fontFamily?.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return; // already loaded
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_MAP?.[fontFamily]}&display=swap`;
  document.head?.appendChild(link);
};

// ── Helper: merge globalTheme + fieldStyle into inline styles for an element ─
const buildElementStyles = (element, globalTheme = {}) => {
  const fs = element?.fieldStyle || {};
  const gt = globalTheme || {};

  // Input/field container styles
  const inputStyle = {
    backgroundColor: fs?.inputBg || gt?.inputBackground || undefined,
    borderColor: fs?.borderColor || gt?.accentColor || undefined,
    borderRadius: fs?.borderRadius != null
      ? `${fs?.borderRadius}px`
      : gt?.borderRadius != null
      ? `${gt?.borderRadius}px`
      : undefined,
    color: fs?.textColor || gt?.inputTextColor || undefined,
    fontFamily: gt?.fontFamily || undefined,
    fontSize: fs?.fontSize != null
      ? `${fs?.fontSize}px`
      : gt?.fontSize
      ? `${gt?.fontSize}px`
      : undefined,
    fontWeight: fs?.inputFontWeight || gt?.fontWeight || undefined,
    height: fs?.inputHeight != null ? `${fs?.inputHeight}px` : undefined,
    paddingLeft: fs?.paddingX != null ? `${fs?.paddingX}px` : undefined,
    paddingRight: fs?.paddingX != null ? `${fs?.paddingX}px` : undefined,
    paddingTop: fs?.paddingY != null ? `${fs?.paddingY}px` : undefined,
    paddingBottom: fs?.paddingY != null ? `${fs?.paddingY}px` : undefined,
  };

  // Label styles
  const labelStyle = {
    color: fs?.labelColor || gt?.labelColor || undefined,
    fontFamily: gt?.fontFamily || undefined,
    fontWeight: fs?.fontWeight || fs?.labelFontWeight || undefined,
    fontStyle: fs?.fontStyle || undefined,
    textDecoration: fs?.textDecoration || undefined,
    textAlign: fs?.contentTextAlign || fs?.labelTextAlign || undefined,
    fontSize: fs?.fontSize != null
      ? `${fs?.fontSize}px`
      : gt?.fontSize
      ? `${gt?.fontSize}px`
      : undefined,
  };

  // Wrapper spacing
  const wrapperStyle = {
    marginBottom: fs?.marginBottom != null ? `${fs?.marginBottom}px` : undefined,
    marginTop: fs?.marginTop != null ? `${fs?.marginTop}px` : undefined,
  };

  return { inputStyle, labelStyle, wrapperStyle };
};

// ── Helper: build form container background style from globalTheme ────────────
const buildFormContainerStyle = (globalTheme = {}) => {
  const gt = globalTheme || {};
  const bgType = gt?.backgroundType || 'solid';

  let background;
  if (bgType === 'gradient') {
    const dir = gt?.gradientDirection || 'to bottom';
    const c1 = gt?.gradientColor1 || '#f0fdf4';
    const c2 = gt?.gradientColor2 || '#dcfce7';
    background = `linear-gradient(${dir}, ${c1}, ${c2})`;
  } else if (bgType === 'image' && gt?.backgroundImage) {
    background = `url(${gt?.backgroundImage}) center/cover no-repeat`;
  } else if (bgType === 'solid' && gt?.backgroundColor) {
    background = gt?.backgroundColor;
  }

  return {
    background: background || undefined,
    fontFamily: gt?.fontFamily || undefined,
  };
};

// ── Mini Edit Toolbar ─────────────────────────────────────────────────────────
const MiniEditToolbar = ({ element, onUpdateElement, onClose }) => {
  // Local state mirrors the element's fieldStyle so controls respond immediately
  const [localStyle, setLocalStyle] = React.useState(() => ({
    fontSize: element?.fieldStyle?.fontSize ?? element?.fontSize ?? 14,
    fontWeight: element?.fieldStyle?.fontWeight ?? element?.fontWeight ?? 'normal',
    fontStyle: element?.fieldStyle?.fontStyle ?? element?.fontStyle ?? 'normal',
    textDecoration: element?.fieldStyle?.textDecoration ?? element?.textDecoration ?? 'none',
    contentTextAlign: element?.fieldStyle?.contentTextAlign ?? element?.fieldStyle?.labelTextAlign ?? element?.textAlign ?? 'left',
  }));

  // Sync local state when element prop changes (e.g. undo/redo or external update)
  React.useEffect(() => {
    setLocalStyle({
      fontSize: element?.fieldStyle?.fontSize ?? element?.fontSize ?? 14,
      fontWeight: element?.fieldStyle?.fontWeight ?? element?.fontWeight ?? 'normal',
      fontStyle: element?.fieldStyle?.fontStyle ?? element?.fontStyle ?? 'normal',
      textDecoration: element?.fieldStyle?.textDecoration ?? element?.textDecoration ?? 'none',
      contentTextAlign: element?.fieldStyle?.contentTextAlign ?? element?.fieldStyle?.labelTextAlign ?? element?.textAlign ?? 'left',
    });
  }, [element?.id]);

  const applyStyle = (patch) => {
    const newLocal = { ...localStyle, ...patch };
    setLocalStyle(newLocal);
    const updatedFieldStyle = {
      ...(element?.fieldStyle || {}),
      fontSize: newLocal?.fontSize,
      fontWeight: newLocal?.fontWeight,
      fontStyle: newLocal?.fontStyle,
      textDecoration: newLocal?.textDecoration,
      contentTextAlign: newLocal?.contentTextAlign,
      // also keep label-level aliases in sync so sidebar doesn't conflict
      labelFontWeight: newLocal?.fontWeight,
    };
    onUpdateElement({
      ...element,
      fieldStyle: updatedFieldStyle,
      // top-level mirrors for legacy rendering paths
      fontSize: newLocal?.fontSize,
      fontWeight: newLocal?.fontWeight,
      fontStyle: newLocal?.fontStyle,
      textDecoration: newLocal?.textDecoration,
      textAlign: newLocal?.contentTextAlign,
    });
  };

  const handleFontSizeChange = (delta) => {
    const newSize = Math.max(8, Math.min(72, localStyle?.fontSize + delta));
    applyStyle({ fontSize: newSize });
  };

  const handleFontSizeInput = (e) => {
    const val = parseInt(e?.target?.value, 10);
    if (!isNaN(val) && val >= 8 && val <= 72) {
      applyStyle({ fontSize: val });
    }
  };

  const handleAlignChange = (align) => {
    applyStyle({ contentTextAlign: align });
  };

  const handleToggleBold = () => {
    applyStyle({ fontWeight: localStyle?.fontWeight === 'bold' ? 'normal' : 'bold' });
  };

  const handleToggleItalic = () => {
    applyStyle({ fontStyle: localStyle?.fontStyle === 'italic' ? 'normal' : 'italic' });
  };

  const handleToggleUnderline = () => {
    applyStyle({ textDecoration: localStyle?.textDecoration === 'underline' ? 'none' : 'underline' });
  };

  const alignments = [
    { value: 'left', icon: 'AlignLeft' },
    { value: 'center', icon: 'AlignCenter' },
    { value: 'right', icon: 'AlignRight' },
  ];

  const isBold = localStyle?.fontWeight === 'bold';
  const isItalic = localStyle?.fontStyle === 'italic';
  const isUnderline = localStyle?.textDecoration === 'underline';

  return (
    <div
      className="flex items-center gap-1 px-2 py-1.5 border border-border rounded-lg shadow-lg"
      onClick={(e) => e?.stopPropagation()}
      style={{
        width: 'fit-content',
        backgroundColor: 'var(--color-card, #ffffff)',
        marginTop: '4px',
        marginLeft: '4px',
        position: 'relative',
        zIndex: 50,
      }}
    >
      {/* Text size */}
      <div className="flex items-center gap-0.5">
        <span className="text-[10px] text-muted-foreground font-medium mr-0.5 select-none">T</span>
        <button
          onClick={() => handleFontSizeChange(-1)}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-foreground"
          title="Decrease font size"
        >
          <Icon name="Minus" size={10} />
        </button>
        <input
          type="number"
          value={localStyle?.fontSize}
          onChange={handleFontSizeInput}
          className="w-9 h-5 text-center text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          min={8}
          max={72}
        />
        <button
          onClick={() => handleFontSizeChange(1)}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-foreground"
          title="Increase font size"
        >
          <Icon name="Plus" size={10} />
        </button>
      </div>
      {/* Divider */}
      <div className="w-px h-4 bg-border mx-0.5" />
      {/* Alignment */}
      <div className="flex items-center gap-0.5">
        {alignments?.map(({ value, icon }) => (
          <button
            key={value}
            onClick={() => handleAlignChange(value)}
            className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
              localStyle?.contentTextAlign === value
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-foreground'
            }`}
            title={`Align ${value}`}
          >
            <Icon name={icon} size={11} />
          </button>
        ))}
      </div>
      {/* Divider */}
      <div className="w-px h-4 bg-border mx-0.5" />
      {/* Bold / Italic / Underline */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={handleToggleBold}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors font-bold text-[11px] ${
            isBold ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'
          }`}
          title="Bold"
        >
          B
        </button>
        <button
          onClick={handleToggleItalic}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors italic text-[11px] ${
            isItalic ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'
          }`}
          title="Italic"
        >
          I
        </button>
        <button
          onClick={handleToggleUnderline}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors underline text-[11px] ${
            isUnderline ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'
          }`}
          title="Underline"
        >
          U
        </button>
      </div>
      {/* Divider */}
      <div className="w-px h-4 bg-border mx-0.5" />
      {/* Close */}
      <button
        onClick={onClose}
        className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-muted-foreground"
        title="Close toolbar"
      >
        <Icon name="X" size={10} />
      </button>
    </div>
  );
};

// ── Mini Image Toolbar ────────────────────────────────────────────────────────
const MiniImageToolbar = ({ element, onUpdateElement, onClose }) => {
  const DEFAULT_SIZE = element?.fieldStyle?.imageSize ?? element?.imageSize ?? 100;
  const [imageSize, setImageSize] = React.useState(DEFAULT_SIZE);

  React.useEffect(() => {
    setImageSize(element?.fieldStyle?.imageSize ?? element?.imageSize ?? 100);
  }, [element?.id]);

  const applySize = (val) => {
    const clamped = Math.max(10, Math.min(200, val));
    setImageSize(clamped);
    onUpdateElement({
      ...element,
      imageSize: clamped,
      fieldStyle: {
        ...(element?.fieldStyle || {}),
        imageSize: clamped,
      },
    });
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg shadow-lg"
      onClick={(e) => e?.stopPropagation()}
      style={{
        width: 'fit-content',
        backgroundColor: 'var(--color-card, #ffffff)',
        marginTop: '4px',
        marginLeft: '4px',
        position: 'relative',
        zIndex: 50,
      }}
    >
      {/* Image icon label */}
      <Icon name="Image" size={13} className="text-muted-foreground flex-shrink-0" />
      {/* Smaller button */}
      <button
        onClick={() => applySize(imageSize - 10)}
        className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-foreground flex-shrink-0"
        title="Smaller"
      >
        <Icon name="Minus" size={10} />
      </button>
      {/* Slider — centered */}
      <input
        type="range"
        min={10}
        max={200}
        value={imageSize}
        onChange={(e) => applySize(Number(e?.target?.value))}
        className="w-28 h-1.5 accent-primary cursor-pointer"
        title={`Image size: ${imageSize}%`}
      />
      {/* Larger button */}
      <button
        onClick={() => applySize(imageSize + 10)}
        className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-foreground flex-shrink-0"
        title="Larger"
      >
        <Icon name="Plus" size={10} />
      </button>
      {/* Size readout */}
      <span className="text-[10px] text-muted-foreground w-8 text-right select-none flex-shrink-0">{imageSize}%</span>
      {/* Divider */}
      <div className="w-px h-4 bg-border mx-0.5" />
      {/* Close */}
      <button
        onClick={onClose}
        className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors text-muted-foreground flex-shrink-0"
        title="Close toolbar"
      >
        <Icon name="X" size={10} />
      </button>
    </div>
  );
};

// ── Layout Mode Badge ─────────────────────────────────────────────────────────
// Exported so the full-preview modal (PreviewModal) can show the same indicator.
// When `onOpen` is omitted the badge renders as a static, non-interactive pill.
export const LayoutModeBadge = ({ layoutMode, stepCount, onOpen }) => {
  if (!layoutMode || layoutMode === 'standard') return null;

  const isConversational = layoutMode === 'conversational';
  const isSplit = layoutMode === 'split';
  const icon = isConversational ? 'MessageCircle' : isSplit ? 'PanelLeft' : 'Layers';
  const label = isConversational
    ? 'Conversational'
    : isSplit
    ? 'Split Page'
    : stepCount > 0
    ? `Multi-Step · ${stepCount} ${stepCount === 1 ? 'page' : 'pages'}`
    : 'Multi-Step';

  const pillClasses =
    'inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 border border-primary/25 rounded-full text-xs font-medium text-primary';

  return (
    <div className="flex items-center justify-center mb-3">
      {onOpen ? (
        <button
          onClick={onOpen}
          title="Click to open Form Layout settings"
          className={`${pillClasses} hover:bg-primary/20 transition-colors cursor-pointer`}
        >
          <Icon name={icon} size={12} />
          {label}
        </button>
      ) : (
        <span className={pillClasses}>
          <Icon name={icon} size={12} />
          {label}
        </span>
      )}
    </div>
  );
};

const FormCanvas = ({ formElements, onElementSelect, selectedElementId, onElementDelete, onDuplicate, multiStepConfig, currentStep, globalTheme = {}, onReorder, onUpdateElement, onPreviewStepChange, layoutMode, onOpenLayoutPanel, splitPanelConfig }) => {
  const [draggedElement, setDraggedElement] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);
  const [dragOverId, setDragOverId] = useState(null);
  const [miniToolbarId, setMiniToolbarId] = useState(null);
  const draggedIdRef = React.useRef(null);

  // Load Google Font whenever fontFamily changes
  useEffect(() => {
    if (globalTheme?.fontFamily) {
      loadGoogleFont(globalTheme?.fontFamily);
    }
  }, [globalTheme?.fontFamily]);

  const isMultiStep = multiStepConfig?.enabled && multiStepConfig?.steps?.length > 0;
  const steps = multiStepConfig?.steps || [];

  // Reset previewStep if it goes out of bounds when steps change
  const safeStep = Math.min(previewStep, Math.max(0, steps?.length - 1));

  // Navigate to a step in the builder preview and notify the parent so
  // the PAGES panel can sync its expanded card.
  const goToPreviewStep = (idx) => {
    const next = Math.max(0, Math.min(idx, Math.max(0, steps?.length - 1)));
    setPreviewStep(next);
    if (typeof onPreviewStepChange === 'function') onPreviewStepChange(next);
  };

  const getStepElements = (step) => {
    if (!step) return formElements;
    const assignedIds = step?.fieldIds || [];
    return formElements?.filter(el => assignedIds?.includes(el?.id));
  };

  const getUnassignedElements = () => {
    const allAssigned = steps?.flatMap(s => s?.fieldIds || []);
    return formElements?.filter(el => !allAssigned?.includes(el?.id));
  };

  const renderFormElement = (element) => {
    const isSelected = element?.id === selectedElementId;
    const showMiniToolbar = isSelected && miniToolbarId === element?.id;
    const { inputStyle, labelStyle, wrapperStyle } = buildElementStyles(element, globalTheme);
    const gt = globalTheme || {};
    const isDragOver = dragOverId === element?.id;

    const baseClasses = `
      group relative p-2 rounded-md border-2 transition-smooth cursor-pointer
      ${isSelected 
        ? 'border-primary bg-primary/5' :'border-transparent hover:border-border hover:bg-muted/30'
      }
      ${isDragOver ? 'border-primary/60 bg-primary/10' : ''}
    `;

    const handleDragStart = (e) => {
      draggedIdRef.current = element?.id;
      setDraggedElement(element?.id);
      e.dataTransfer.effectAllowed = 'move';
      e?.dataTransfer?.setData('text/plain', element?.id);
    };

    const handleDragOver = (e) => {
      e?.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (draggedIdRef?.current && draggedIdRef?.current !== element?.id) {
        setDragOverId(element?.id);
      }
    };

    const handleDragLeave = (e) => {
      if (!e?.currentTarget?.contains(e?.relatedTarget)) {
        setDragOverId(null);
      }
    };

    const handleDrop = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      const fromId = draggedIdRef?.current;
      const toId = element?.id;
      setDragOverId(null);
      if (fromId && toId && fromId !== toId && onReorder) {
        const fromIndex = formElements?.findIndex(el => el?.id === fromId);
        const toIndex = formElements?.findIndex(el => el?.id === toId);
        if (fromIndex !== -1 && toIndex !== -1) {
          const newElements = [...formElements];
          const [moved] = newElements?.splice(fromIndex, 1);
          newElements?.splice(toIndex, 0, moved);
          onReorder(newElements);
        }
      }
      draggedIdRef.current = null;
      setDraggedElement(null);
    };

    const handleDragEnd = () => {
      draggedIdRef.current = null;
      setDraggedElement(element?.id === draggedElement ? null : draggedElement);
      setDragOverId(null);
    };

    const handleElementClick = () => {
      if (onElementSelect) onElementSelect(element);
      setMiniToolbarId(element?.id);
    };

    const handleMiniToolbarUpdate = (updatedElement) => {
      if (onUpdateElement) onUpdateElement(updatedElement);
    };

    const renderElementContent = () => {
      switch (element?.type) {
        case 'input':
        case 'email':
        case 'phone':
        case 'number':
        case 'date': {
          const iconName    = element?.fieldStyle?.iconName;
          const iconPos     = element?.fieldStyle?.iconPosition || 'left';
          const padX        = element?.fieldStyle?.paddingX ?? 12;
          const padY        = element?.fieldStyle?.paddingY ?? 8;
          const iconPadding = iconName ? padX + 22 : padX;
          const defaultPlaceholder =
            element?.type === 'email'  ? (element?.placeholder || 'you@example.com') :
            element?.type === 'phone'  ? (element?.placeholder || '+1 (555) 000-0000') :
            element?.type === 'number' ? (element?.placeholder || '0') :
            element?.type === 'date'   ? '' :
            (element?.placeholder || 'Enter text...');
          const defaultLabel =
            element?.type === 'email'  ? (element?.label || 'Email') :
            element?.type === 'phone'  ? (element?.label || 'Phone') :
            element?.type === 'number' ? (element?.label || 'Number') :
            element?.type === 'date'   ? (element?.label || 'Date') :
            (element?.label || 'Label Text');
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {defaultLabel}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="relative">
                <div
                  className="w-full border rounded-md text-sm text-muted-foreground flex items-center"
                  style={{
                    ...inputStyle,
                    paddingTop:    `${padY}px`,
                    paddingBottom: `${padY}px`,
                    paddingLeft:  iconName && iconPos === 'left'  ? `${iconPadding}px` : `${padX}px`,
                    paddingRight: iconName && iconPos === 'right' ? `${iconPadding}px` : `${padX}px`,
                    minHeight: inputStyle?.height || '40px',
                  }}
                >
                  {defaultPlaceholder}
                </div>
                {iconName && (
                  <div
                    className="absolute inset-y-0 flex items-center pointer-events-none"
                    style={{ [iconPos === 'right' ? 'right' : 'left']: `${padX}px` }}
                  >
                    <Icon name={iconName} size={15} color={element?.fieldStyle?.iconColor || inputStyle?.color || 'var(--color-muted-foreground)'} />
                  </div>
                )}
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );
        }

        case 'textarea':
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {element?.label || 'Paragraph'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div
                className="w-full border rounded-md text-sm text-muted-foreground"
                style={{
                  ...inputStyle,
                  padding: (inputStyle?.paddingLeft || inputStyle?.paddingTop)
                    ? `${element?.fieldStyle?.paddingY ?? 8}px ${element?.fieldStyle?.paddingX ?? 12}px`
                    : '8px 12px',
                  minHeight: `${element?.rows ? element.rows * 24 + 16 : 88}px`,
                  resize: 'none',
                }}
              >
                {element?.placeholder || 'Enter your comments here...'}
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );

        case 'select':
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label
                className="text-sm font-medium block"
                style={labelStyle}
              >
                {element?.label || 'Select Option'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div
                className="w-full border rounded-md text-sm text-muted-foreground flex items-center justify-between"
                style={{
                  ...inputStyle,
                  padding: `${element?.fieldStyle?.paddingY ?? 8}px ${element?.fieldStyle?.paddingX ?? 12}px`,
                  minHeight: inputStyle?.height || '40px',
                }}
              >
                <span>{element?.placeholder || 'Choose an option...'}</span>
                <Icon name="ChevronDown" size={16} />
              </div>
            </div>
          );

        case 'checkbox': case 'radio':
          return (
            <div className="space-y-3" style={wrapperStyle}>
              <label
                className="text-sm font-medium block"
                style={labelStyle}
              >
                {element?.label || 'Select Options'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="space-y-2">
                {(element?.options || ['Option 1', 'Option 2', 'Option 3'])?.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className={`
                      w-4 h-4 border-2 border-input flex items-center justify-center
                      ${element?.type === 'radio' ? 'rounded-full' : 'rounded'}
                    `}
                    style={{ borderColor: inputStyle?.borderColor || undefined }}
                    >
                      {idx === 0 && (
                        <div className={`
                          bg-primary
                          ${element?.type === 'radio' ? 'w-2 h-2 rounded-full' : 'w-3 h-3 rounded'}
                        `} />
                      )}
                    </div>
                    <span className="text-sm text-foreground" style={{ color: inputStyle?.color || undefined, fontFamily: inputStyle?.fontFamily || undefined }}>{option}</span>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'file':
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label
                className="text-sm font-medium block"
                style={labelStyle}
              >
                {element?.label || 'Upload File'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="w-full p-6 bg-muted/50 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center gap-2"
                style={{ borderRadius: inputStyle?.borderRadius || undefined }}
              >
                <Icon name="Upload" size={32} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground">Click or drag files here</p>
              </div>
            </div>
          );

        case 'signature':
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label
                className="text-sm font-medium block"
                style={labelStyle}
              >
                {element?.label || 'Signature'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="w-full p-6 bg-muted/50 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center gap-2"
                style={{ borderRadius: inputStyle?.borderRadius || undefined }}
              >
                <Icon name="Signature" size={32} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground">Click to sign</p>
              </div>
            </div>
          );

        case 'button':
          return (
            <div className="flex items-center justify-start py-1" style={wrapperStyle}>
              <button
                style={{
                  backgroundColor: element?.buttonColor || gt?.accentColor || '#0e7572',
                  color: element?.buttonTextColor || '#FFFFFF',
                  width: `${element?.buttonWidth || 200}px`,
                  height: `${element?.buttonHeight || 44}px`,
                  borderRadius: inputStyle?.borderRadius || `${element?.borderRadius || 6}px`,
                  fontSize: inputStyle?.fontSize || `${element?.fontSize || 16}px`,
                  fontWeight: element?.fontWeight || '500',
                  fontFamily: inputStyle?.fontFamily || undefined,
                  border: 'none',
                  cursor: 'pointer',
                  opacity: element?.disabled ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  pointerEvents: 'none',
                }}
                className="shadow-sm"
              >
                {element?.buttonText || 'Submit'}
              </button>
            </div>
          );

        case 'layout':
          if (element?.id === 'section') {
            return (
              <div className="p-4 bg-muted/30 border border-dashed border-border rounded-md" style={wrapperStyle}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Box" size={18} color="var(--color-muted-foreground)" />
                  <span className="text-sm font-medium text-foreground">
                    {element?.label || 'Section Container'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Drop elements here</p>
              </div>
            );
          }
          if (element?.id === 'columns') {
            return (
              <div className="grid grid-cols-2 gap-4" style={wrapperStyle}>
                {[1, 2]?.map((col) => (
                  <div key={col} className="p-4 bg-muted/30 border border-dashed border-border rounded-md">
                    <p className="text-xs text-muted-foreground text-center">Column {col}</p>
                  </div>
                ))}
              </div>
            );
          }
          if (element?.id === 'divider') {
            return <div className="w-full h-px bg-border" style={wrapperStyle} />;
          }
          break;

        case 'content':
          if (element?.id?.startsWith('heading') || element?.contentType === 'heading') {
            return (
              <h3
                style={{
                  color: element?.labelColor || labelStyle?.color || 'var(--color-foreground)',
                  fontFamily: labelStyle?.fontFamily || undefined,
                  fontSize: element?.fieldStyle?.fontSize != null
                    ? `${element?.fieldStyle?.fontSize}px`
                    : element?.fontSize
                    ? `${element?.fontSize}px`
                    : (inputStyle?.fontSize || undefined),
                  fontWeight: element?.fieldStyle?.fontWeight || element?.fontWeight || '600',
                  fontStyle: element?.fieldStyle?.fontStyle || element?.fontStyle || undefined,
                  textDecoration: element?.fieldStyle?.textDecoration || element?.textDecoration || undefined,
                  textAlign: element?.fieldStyle?.contentTextAlign || element?.fieldStyle?.labelTextAlign || element?.textAlign || undefined,
                  ...wrapperStyle,
                }}
                className="font-heading"
              >
                {element?.content || element?.label || 'Heading Text'}
              </h3>
            );
          }
          if (element?.id?.startsWith('paragraph') || element?.contentType === 'paragraph') {
            return (
              <p
                style={{
                  color: element?.labelColor || labelStyle?.color || 'var(--color-foreground)',
                  fontFamily: labelStyle?.fontFamily || undefined,
                  fontSize: element?.fieldStyle?.fontSize != null
                    ? `${element?.fieldStyle?.fontSize}px`
                    : element?.fontSize
                    ? `${element?.fontSize}px`
                    : (inputStyle?.fontSize || undefined),
                  fontWeight: element?.fieldStyle?.fontWeight || element?.fontWeight || '400',
                  fontStyle: element?.fieldStyle?.fontStyle || element?.fontStyle || undefined,
                  textDecoration: element?.fieldStyle?.textDecoration || element?.textDecoration || undefined,
                  textAlign: element?.fieldStyle?.contentTextAlign || element?.fieldStyle?.labelTextAlign || element?.textAlign || undefined,
                  ...wrapperStyle,
                }}
                className="leading-relaxed"
              >
                {element?.content || 'Paragraph text goes here. You can customize this content in the settings panel.'}
              </p>
            );
          }
          // Fallback for any content type
          return (
            <p
              style={{
                color: element?.labelColor || labelStyle?.color || 'var(--color-foreground)',
                fontFamily: labelStyle?.fontFamily || undefined,
                fontSize: element?.fieldStyle?.fontSize != null
                  ? `${element?.fieldStyle?.fontSize}px`
                  : element?.fontSize
                  ? `${element?.fontSize}px`
                  : (inputStyle?.fontSize || undefined),
                fontWeight: element?.fieldStyle?.fontWeight || element?.fontWeight || '400',
                fontStyle: element?.fieldStyle?.fontStyle || element?.fontStyle || undefined,
                textDecoration: element?.fieldStyle?.textDecoration || element?.textDecoration || undefined,
                textAlign: element?.fieldStyle?.contentTextAlign || element?.fieldStyle?.labelTextAlign || element?.textAlign || undefined,
                ...wrapperStyle,
              }}
              className="leading-relaxed"
            >
              {element?.content || 'Content text goes here.'}
            </p>
          );

        case 'media':
          if (element?.id?.startsWith('image') || element?.type === 'media') {
            const imgSize = element?.fieldStyle?.imageSize ?? element?.imageSize ?? 100;
            return (
              <div className="w-full overflow-hidden rounded-md flex justify-center" style={wrapperStyle}>
                <Image
                  src={element?.src || '/assets/images/web-forms-logo-1769800970615.png'}
                  alt={element?.alt || 'Form header image'}
                  style={{ width: `${imgSize}%`, objectFit: 'contain' }}
                />
              </div>
            );
          }
          break;

        case 'toggle': {
          const isOn = true; // illustrative "on" state in canvas preview
          const toggleColor = inputStyle?.borderColor || gt?.accentColor || '#0e7572';
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {element?.label || 'Toggle Switch'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="flex items-center gap-3">
                {/* Pill toggle — illustrative on state */}
                <div
                  className="relative flex-shrink-0 w-11 h-6 rounded-full"
                  style={{ backgroundColor: toggleColor }}
                >
                  <div className="absolute top-1 right-1 w-4 h-4 bg-white rounded-full shadow-sm" />
                </div>
                <span className="text-sm" style={{ color: inputStyle?.color || 'var(--color-foreground)' }}>
                  {element?.onLabel || 'On'}
                </span>
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );
        }

        case 'rating': {
          const ratingMax = Math.max(1, element?.ratingMax ?? 5);
          const ratingDefault = Math.min(ratingMax, Math.max(1, element?.ratingDefault ?? Math.ceil(ratingMax / 2)));
          const starColor = inputStyle?.borderColor || gt?.accentColor || '#0e7572';
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {element?.label || 'Star Rating'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: ratingMax }, (_, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: '26px',
                      color: i < ratingDefault ? starColor : '#d1d5db',
                      lineHeight: 1,
                    }}
                  >★</span>
                ))}
                <span className="text-sm ml-2" style={{ color: inputStyle?.color || 'var(--color-muted-foreground)' }}>
                  {ratingDefault} / {ratingMax}
                </span>
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );
        }

        case 'scale': {
          const scaleMin = element?.scaleMin ?? 1;
          const scaleMax = Math.max(scaleMin + 1, element?.scaleMax ?? 10);
          const scaleDefault = Math.min(scaleMax, Math.max(scaleMin, element?.scaleDefault ?? Math.round((scaleMin + scaleMax) / 2)));
          const scaleColor = inputStyle?.borderColor || gt?.accentColor || '#0e7572';
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {element?.label || 'Numeric Scale'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => {
                  const val = scaleMin + i;
                  const isSelected = val === scaleDefault;
                  return (
                    <div
                      key={i}
                      className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium"
                      style={{
                        backgroundColor: isSelected ? scaleColor : undefined,
                        borderColor: isSelected ? scaleColor : (inputStyle?.borderColor || 'var(--color-border)'),
                        color: isSelected ? '#fff' : (inputStyle?.color || 'var(--color-foreground)'),
                      }}
                    >
                      {val}
                    </div>
                  );
                })}
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );
        }

        case 'slider': {
          const sliderMin = element?.sliderMin ?? 0;
          const sliderMax = element?.sliderMax ?? 100;
          const sliderStep = element?.sliderStep ?? 1;
          const sliderDefault = element?.sliderDefault ?? Math.round((sliderMin + sliderMax) / 2);
          const sliderColor = inputStyle?.borderColor || gt?.accentColor || '#0e7572';
          return (
            <div className="space-y-2" style={wrapperStyle}>
              <label className="text-sm font-medium block" style={labelStyle}>
                {element?.label || 'Slider'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                    defaultValue={sliderDefault}
                    readOnly
                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                    style={{ accentColor: sliderColor }}
                  />
                  <span
                    className="text-sm font-medium w-9 text-right flex-shrink-0"
                    style={{ color: inputStyle?.color || 'var(--color-foreground)' }}
                  >
                    {sliderDefault}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{sliderMin}</span>
                  <span>{sliderMax}</span>
                </div>
              </div>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
            </div>
          );
        }

        default:
          return (
            <div className="flex items-center gap-3" style={wrapperStyle}>
              <Icon name={element?.icon || 'Box'} size={20} color="var(--color-muted-foreground)" />
              <span className="text-sm text-foreground">{element?.name}</span>
            </div>
          );
      }
    };

    return (
      <React.Fragment>
        <div
          className={baseClasses}
          onClick={handleElementClick}
          draggable
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          style={{ opacity: draggedElement === element?.id ? 0.45 : 1, transition: 'opacity 0.15s ease' }}
        >
          {/* Drag handle */}
          <div
            className={`absolute left-1.5 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 transition-opacity cursor-grab active:cursor-grabbing ${isSelected ? 'opacity-40' : 'opacity-0 group-hover:opacity-30'}`}
            onMouseDown={e => e?.stopPropagation()}
            title="Drag to reorder"
          >
            <div className="w-3.5 h-0.5 bg-muted-foreground rounded-full" />
            <div className="w-3.5 h-0.5 bg-muted-foreground rounded-full" />
            <div className="w-3.5 h-0.5 bg-muted-foreground rounded-full" />
          </div>
          {renderElementContent()}
          <div className={`absolute top-2 right-2 flex items-center gap-1 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                if (onDuplicate) onDuplicate(element?.id);
              }}
              className="p-1.5 bg-muted hover:bg-muted/80 rounded-md transition-smooth"
              aria-label="Duplicate element"
              title="Duplicate element"
            >
              <Icon name="Copy" size={14} color="var(--color-muted-foreground)" />
            </button>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                if (onElementDelete) onElementDelete(element?.id);
              }}
              className="p-1.5 bg-destructive/10 hover:bg-destructive/20 rounded-md transition-smooth"
              aria-label="Delete element"
            >
              <Icon name="Trash2" size={14} color="var(--color-destructive)" />
            </button>
          </div>
        </div>
        {/* Mini Toolbar — image elements get MiniImageToolbar, others get MiniEditToolbar */}
        {showMiniToolbar && onUpdateElement && element?.type === 'media' && (
          <MiniImageToolbar
            element={element}
            onUpdateElement={handleMiniToolbarUpdate}
            onClose={() => setMiniToolbarId(null)}
          />
        )}
        {showMiniToolbar && onUpdateElement && element?.type !== 'media' && (
          <MiniEditToolbar
            element={element}
            onUpdateElement={handleMiniToolbarUpdate}
            onClose={() => setMiniToolbarId(null)}
          />
        )}
      </React.Fragment>
    );
  };

  // ── Multi-step canvas ──────────────────────────────────────────────────────
  const renderMultiStepCanvas = () => {
    const currentStep = steps?.[safeStep];
    const stepElements = getStepElements(currentStep);
    const unassigned = getUnassignedElements();
    const progressStyle = multiStepConfig?.progressStyle || 'bar';
    const progressPosition = multiStepConfig?.progressPosition || 'top';
    const showStepTitles = multiStepConfig?.showStepTitles !== false;
    const showStepCount = multiStepConfig?.showStepCount !== false;
    const nextText = multiStepConfig?.nextButtonText || 'Next';
    const prevText = multiStepConfig?.prevButtonText || 'Previous';
    const submitText = multiStepConfig?.submitButtonText || 'Submit';
    const isLastStep = safeStep === steps?.length - 1;
    const isFirstStep = safeStep === 0;

    const renderProgress = () => {
      if (progressStyle === 'none' || progressStyle === 'footer-bar') return null;
      return (
        <div className="mb-4">
          {progressStyle === 'bar' && (
            <div className="space-y-1">
              {showStepCount && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Step {safeStep + 1} of {steps?.length}</span>
                  <span>{Math.round(((safeStep + 1) / steps?.length) * 100)}%</span>
                </div>
              )}
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${((safeStep + 1) / steps?.length) * 100}%` }}
                />
              </div>
            </div>
          )}
          {progressStyle === 'steps' && (
            <div className="flex items-center gap-1">
              {steps?.map((step, idx) => (
                <React.Fragment key={step?.id}>
                  <button
                    onClick={() => multiStepConfig?.allowStepJump && setPreviewStep(idx)}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                      idx < safeStep ? 'bg-primary text-primary-foreground' :
                      idx === safeStep ? 'bg-primary text-primary-foreground ring-2 ring-primary/30': 'bg-muted text-muted-foreground'
                    } ${multiStepConfig?.allowStepJump ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                    title={showStepTitles ? step?.title : undefined}
                  >
                    {idx < safeStep ? <Icon name="Check" size={12} /> : idx + 1}
                  </button>
                  {idx < steps?.length - 1 && (
                    <div className={`flex-1 h-0.5 transition-colors ${idx < safeStep ? 'bg-primary' : 'bg-muted'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {progressStyle === 'numbered' && (
            <div className="flex items-center gap-2 flex-wrap">
              {steps?.map((step, idx) => (
                <button
                  key={step?.id}
                  onClick={() => multiStepConfig?.allowStepJump && setPreviewStep(idx)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    idx === safeStep ? 'bg-primary text-primary-foreground' :
                    idx < safeStep ? 'bg-primary/20 text-primary': 'bg-muted text-muted-foreground'
                  } ${multiStepConfig?.allowStepJump ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                >
                  <span>{idx + 1}</span>
                  {showStepTitles && <span>{step?.title}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    };

    const containerStyle = buildFormContainerStyle(globalTheme);

    return (
      <div className="flex-1 overflow-y-auto scrollbar-custom bg-muted/30">
        <div className="w-full px-4 py-4">
          {/* Layout mode badge */}
          <LayoutModeBadge
            layoutMode={layoutMode}
            stepCount={steps?.length}
            onOpen={() => typeof onOpenLayoutPanel === 'function' && onOpenLayoutPanel()}
          />
          {/* Step tabs for builder navigation */}
          <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
            {steps?.map((step, idx) => (
              <button
                key={step?.id}
                onClick={() => goToPreviewStep(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors border ${
                  idx === safeStep
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${idx === safeStep ? 'bg-white/20' : 'bg-muted'}`}>
                  {idx + 1}
                </span>
                {step?.title}
                <span className="ml-0.5 text-[10px] opacity-60">({getStepElements(step)?.length})</span>
              </button>
            ))}
          </div>

          <div
            className={`rounded-lg shadow-elevation-md border border-border mx-auto overflow-hidden ${progressStyle === 'footer-bar' ? 'pt-4 lg:pt-6 px-4 lg:px-6 pb-0' : 'p-4 lg:p-6'}`}
            style={{
              fontFamily: containerStyle?.fontFamily || undefined,
              background: containerStyle?.background || '#ffffff',
              maxWidth: globalTheme?.formMaxWidth ? `${globalTheme.formMaxWidth}px` : '80%',
              ...(progressStyle === 'footer-bar' ? {
                borderBottomLeftRadius: `${multiStepConfig?.footerBorderRadius ?? 0}px`,
                borderBottomRightRadius: `${multiStepConfig?.footerBorderRadius ?? 0}px`,
              } : {}),
            }}
          >
            {/* Progress indicator */}
            {progressPosition === 'top' && renderProgress()}

            {/* Step title */}
            {showStepTitles && currentStep?.title && (
              <div className="mb-4 pb-3 border-b border-border">
                <h3 className="text-base font-heading font-semibold text-foreground">{currentStep?.title}</h3>
                {currentStep?.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">{currentStep?.description}</p>
                )}
              </div>
            )}

            {/* Step fields */}
            {stepElements?.length === 0 ? (
              <div className="min-h-[300px] flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md">
                <Icon name="Layers" size={36} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  No fields assigned to this step yet.<br />
                  Use the Multi-Step panel to assign fields.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {stepElements?.map((element) => (
                  <div key={element?.id}>
                    {renderFormElement(element)}
                  </div>
                ))}
              </div>
            )}

            {progressStyle === 'footer-bar' ? (
              /* Footer Bar navigation — full-width dark bar flush at card bottom */
              <div
                className="flex items-center justify-between px-6 py-3 mt-6 -mx-4 lg:-mx-6 relative"
                style={{ background: globalTheme?.footerColor || darkenHex(globalTheme?.accentColor || globalTheme?.themeColor || globalTheme?.primaryColor || '') }}
              >
                {!isFirstStep ? (
                  <button
                    onClick={() => goToPreviewStep(Math.max(0, safeStep - 1))}
                    className="flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white transition-colors"
                  >
                    <Icon name="ChevronLeft" size={16} />
                    {prevText}
                  </button>
                ) : <span />}
                {showStepCount && (
                  <span className="text-xs text-white/60 absolute left-1/2 -translate-x-1/2 pointer-events-none">
                    Step {safeStep + 1} of {steps?.length}
                  </span>
                )}
                <button
                  onClick={() => !isLastStep && goToPreviewStep(Math.min(steps?.length - 1, safeStep + 1))}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/80 transition-colors"
                >
                  {isLastStep ? submitText : nextText}
                  {!isLastStep && <Icon name="ChevronRight" size={16} />}
                </button>
              </div>
            ) : (
              <>
                {/* Navigation buttons */}
                <div className={`flex items-center mt-6 pt-4 border-t border-border ${isFirstStep ? 'justify-end' : 'justify-between'}`}>
                  {!isFirstStep && (
                    <button
                      onClick={() => setPreviewStep(s => Math.max(0, s - 1))}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-border rounded-md bg-background hover:bg-muted transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} />
                      {prevText}
                    </button>
                  )}
                  <button
                    onClick={() => !isLastStep && setPreviewStep(s => Math.min(steps?.length - 1, s + 1))}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {isLastStep ? submitText : nextText}
                    {!isLastStep && <Icon name="ChevronRight" size={16} />}
                  </button>
                </div>

                {/* Progress indicator bottom */}
                {progressPosition === 'bottom' && <div className="mt-4">{renderProgress()}</div>}
              </>
            )}
          </div>

          {/* Unassigned fields warning */}
          {unassigned?.length > 0 && (
            <div className="mt-3 max-w-[80%] mx-auto p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} color="#92400e" className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800">
                  {unassigned?.length} field{unassigned?.length !== 1 ? 's' : ''} not assigned to any step
                </p>
                <p className="text-xs text-amber-700 mt-0.5">Open the Multi-Step panel to assign them to a step.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Flat canvas (default) ──────────────────────────────────────────────────
  const containerStyle = buildFormContainerStyle(globalTheme);

  if (isMultiStep) {
    return renderMultiStepCanvas();
  }

  // ── Split-page canvas ──────────────────────────────────────────────────────
  if (layoutMode === 'split') {
    const spc = splitPanelConfig || {};
    const panelSide = spc?.panelSide || 'left';
    const ratio = Math.min(80, Math.max(20, spc?.widthRatio || 40));
    const formRatio = 100 - ratio;

    const panelColumn = (
      <div
        style={{
          flex: `0 0 ${ratio}%`,
          minHeight: 400,
          background: 'var(--color-muted)',
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid var(--color-border)',
        }}
      >
        <SplitPanelContent config={spc} preview />
      </div>
    );

    const formColumn = (
      <div style={{ flex: `0 0 ${formRatio}%`, minWidth: 0 }}>
        <div
          className="rounded-lg shadow-elevation-md border border-border p-4 lg:p-6"
          style={{ ...containerStyle, background: containerStyle?.background || 'var(--color-card)' }}
        >
          {formElements?.length === 0 ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md">
              <Icon name="MousePointerClick" size={36} color="var(--color-muted-foreground)" />
              <h3 className="text-base font-heading font-semibold text-foreground mt-3">
                Add form fields here
              </h3>
              <p className="text-xs text-muted-foreground mt-1 text-center">
                Click elements from the left panel
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {formElements?.map((element) => (
                <div key={element?.id}>
                  {renderFormElement(element)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div className="flex-1 overflow-y-auto scrollbar-custom bg-muted/30">
        <div className="w-full px-4 py-4">
          <LayoutModeBadge
            layoutMode={layoutMode}
            stepCount={0}
            onOpen={() => typeof onOpenLayoutPanel === 'function' && onOpenLayoutPanel()}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
            {panelSide === 'left' ? <>{panelColumn}{formColumn}</> : <>{formColumn}{panelColumn}</>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-custom bg-muted/30">
      <div className="w-full px-4 py-4">
        {/* Layout mode badge — shown above the form card when not in Standard mode */}
        <LayoutModeBadge
          layoutMode={layoutMode}
          stepCount={0}
          onOpen={() => typeof onOpenLayoutPanel === 'function' && onOpenLayoutPanel()}
        />
        <div
          className="rounded-lg shadow-elevation-md border border-border p-4 lg:p-6 mx-auto"
          style={{ ...containerStyle, background: containerStyle?.background || 'var(--color-card)', maxWidth: globalTheme?.formMaxWidth ? `${globalTheme.formMaxWidth}px` : '80%' }}
        >
          {formElements?.length === 0 ? (
            <div className="h-full min-h-[500px] flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md">
              <Icon name="MousePointerClick" size={48} color="var(--color-muted-foreground)" />
              <h3 className="text-lg font-heading font-semibold text-foreground mt-4">
                Start Building Your Form
              </h3>
              <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">
                Click elements from the left panel to add them to your form
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {formElements?.map((element, index) => (
                <div key={element?.id}>
                  {renderFormElement(element)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormCanvas;